import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaTimes, FaCheck, FaCrown, FaStar } from 'react-icons/fa';
import { api } from '../../services/api';
const FEATURE_MAPPING = {
  '所有 Free 功能': 'membership.featuresList.allFree',
  'AI 预测分析': 'membership.featuresList.aiPredictions',
  '聪明钱实时动态': 'membership.featuresList.smartMoneyLive',
  '多平台赔率对比': 'membership.featuresList.oddsComparison',
  '赔率历史图表': 'membership.featuresList.oddsHistory',
  '近期状态分析': 'membership.featuresList.formAnalysis',
  '伤病监测': 'membership.featuresList.injuryMonitoring',
  '所有 Lite 功能': 'membership.featuresList.allLite',
  '鲸鱼持仓分析': 'membership.featuresList.whaleAnalysis',
  '聪明钱持仓详情': 'membership.featuresList.smartMoneyDetails',
  '完整 AI 分析报告': 'membership.featuresList.aiReport',
  '球队高级统计': 'membership.featuresList.teamStats',
  '优先客服支持': 'membership.featuresList.prioritySupport'
};

/**
 * 会员升级弹窗组件
 */
export function UpgradeModal({ isOpen, onClose, currentTier = 'free' }) {
  const { t } = useTranslation();
  const [tiers, setTiers] = useState([]);
  const [billingPeriod, setBillingPeriod] = useState('monthly'); // monthly | yearly
  const [loadingTier, setLoadingTier] = useState(null);
  const [nowpaymentsOptions, setNowpaymentsOptions] = useState([]);
  const [selectedChainId, setSelectedChainId] = useState(null);
  const [selectedTokenSymbol, setSelectedTokenSymbol] = useState(null);
  const [showPaymentOptions, setShowPaymentOptions] = useState(false);
  const [pendingTierName, setPendingTierName] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutInfo, setCheckoutInfo] = useState(null);
  const [checkoutCopied, setCheckoutCopied] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadTiers();
      loadNowpaymentsOptions();
    }
  }, [isOpen]);

  // 关闭时重置状态
  useEffect(() => {
    if (!isOpen) {
      setShowPaymentOptions(false);
      setPendingTierName(null);
      setShowCheckout(false);
      setCheckoutInfo(null);
      setCheckoutCopied(false);
    }
  }, [isOpen]);

  const loadTiers = async () => {
    try {
      const data = await api.getMembershipTiers();
      if (data.code === 'SUCCESS') {
        const sortedTiers = data.data.tiers.filter((t) => t.name !== 'free').sort((a, b) => {
          if (a.name === 'lite') return -1;
          if (b.name === 'lite') return 1;
          return 0;
        });
        setTiers(sortedTiers);
      }
    } catch (err) {
      console.error('Failed to load tiers:', err);
    }
  };

  const loadNowpaymentsOptions = async () => {
    try {
      const data = await api.getNowpaymentsOptions();
      if (data.code === 'SUCCESS') {
        const chains = data.data?.chains || [];
        const defaultChainId = data.data?.default_chain_id || chains[0]?.id || null;
        const defaultChain = chains.find((chain) => chain.id === defaultChainId) || chains[0];
        const defaultTokenSymbol =
          data.data?.default_token_symbol ||
          (defaultChain?.tokens ? Object.keys(defaultChain.tokens)[0] : null);

        setNowpaymentsOptions(chains);
        setSelectedChainId(defaultChainId || null);
        setSelectedTokenSymbol(defaultTokenSymbol || null);
      }
    } catch (err) {
      console.error('Failed to load NOWPayments options:', err);
    }
  };

  const selectedChain = nowpaymentsOptions.find((chain) => chain.id === selectedChainId);
  const selectedPayCurrency = selectedChain?.tokens?.[selectedTokenSymbol] || null;

  // 点击升级按钮 - 显示支付选项
  const handleUpgradeClick = (tierName) => {
    setPendingTierName(tierName);
    setShowPaymentOptions(true);
  };

  // 确认支付
  const handleConfirmPayment = async () => {
    if (!pendingTierName) return;
    
    setLoadingTier(pendingTierName);
    const popup = window.open('about:blank', '_blank');
    if (popup) {
      popup.opener = null;
    }
    try {
      const periodMonths = billingPeriod === 'yearly' ? 12 : 1;
      const data = await api.createCheckout(pendingTierName, periodMonths, selectedPayCurrency);

      if (data.code === 'SUCCESS') {
        const orderId = data.data?.payment_order_id;
        if (!orderId) {
          if (popup) {
            popup.close();
          }
          return;
        }
        const paymentUrl = `/payment/${orderId}`;
        const popupBlocked = !popup;
        const amount = data.data?.amount ?? getPendingTierPrice();
        const currency = data.data?.currency ?? 'USD';
        const chainLabel = (data.data?.nowpayments?.chain || selectedChain?.label || selectedChainId || '')
          .toString()
          .toUpperCase();
        const tokenLabel = (data.data?.nowpayments?.token || selectedTokenSymbol || '')
          .toString()
          .toUpperCase();
        const payLabel = tokenLabel && chainLabel ? `${tokenLabel} (${chainLabel})` : tokenLabel || chainLabel || '-';
        if (popup) {
          popup.location.href = paymentUrl;
          popup.focus();
        }
        const absoluteUrl = `${window.location.origin}${paymentUrl}`;
        setCheckoutInfo({
          amount,
          currency,
          payLabel,
          paymentUrl: absoluteUrl,
          popupBlocked,
        });
        setShowCheckout(true);
        setShowPaymentOptions(false);
        setPendingTierName(null);
      } else if (popup) {
        popup.close();
      }
    } catch (err) {
      if (popup) {
        popup.close();
      }
      console.error('Failed to create checkout:', err);
    }
    setLoadingTier(null);
  };

  const handleOpenPaymentPage = () => {
    if (!checkoutInfo?.paymentUrl) return;
    const newPopup = window.open(checkoutInfo.paymentUrl, '_blank', 'noopener');
    if (newPopup) {
      newPopup.opener = null;
      newPopup.focus();
    } else {
      window.location.assign(checkoutInfo.paymentUrl);
    }
  };

  const handleCopy = async (value, setFlag) => {
    try {
      await navigator.clipboard.writeText(value);
      setFlag(true);
      window.setTimeout(() => setFlag(false), 1500);
    } catch (err) {
      setFlag(false);
    }
  };

  const getTranslatedFeature = (feature) => {
    const key = FEATURE_MAPPING[feature];
    return key ? t(key) : feature;
  };

  const getPendingTierPrice = () => {
    const tier = tiers.find(t => t.name === pendingTierName);
    if (!tier) return 0;
    return billingPeriod === 'yearly' ? tier.price_yearly : tier.price_monthly;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* 背景遮罩 */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-md transition-opacity duration-300"
        onClick={onClose}
      />

      {/* 弹窗内容 */}
      <div className="relative bg-surface/90 backdrop-blur-xl rounded-3xl w-full max-w-4xl mx-auto overflow-hidden border border-glass-border shadow-[0_20px_40px_rgba(8,12,18,0.35)] transform transition-all animate-in fade-in zoom-in-95 duration-300 max-h-[90vh] overflow-y-auto">

        {/* Decorative elements */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-32 bg-gradient-to-b from-accent/10 to-transparent blur-3xl pointer-events-none" />

        {/* Header */}
        <div className="relative p-6 text-center">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-xl text-gray-400 hover:text-white hover:bg-white/5 transition-all duration-200"
          >
            <FaTimes size={18} />
          </button>

          <div className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-gradient-to-br from-accent-warm to-accent-soft mb-3 shadow-[0_10px_18px_rgba(8,12,18,0.35)]">
            <FaCrown className="text-white text-lg" />
          </div>

          <h2 className="text-2xl font-bold text-white mb-1">
            {t('membership.upgradeTitle', 'Upgrade Membership')}
          </h2>
          <p className="text-gray-400 text-sm">
            {t('membership.upgradeSubtitle', 'Unlock premium features and get accurate AI predictions')}
          </p>

          {/* 计费周期切换 */}
          <div className="mt-5 flex justify-center">
            <div className="bg-white/5 p-1 rounded-xl flex items-center relative border border-white/5">
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={`flex-1 px-5 py-1.5 rounded-lg text-sm font-medium transition-all duration-300 ${billingPeriod === 'monthly'
                  ? 'bg-gradient-to-br from-accent-strong to-accent text-white shadow-[0_10px_18px_rgba(8,12,18,0.35)]'
                  : 'text-gray-400 hover:text-white'
                  }`}
              >
                {t('membership.monthly', 'Monthly')}
              </button>
              <button
                onClick={() => setBillingPeriod('yearly')}
                className={`flex-1 px-5 py-1.5 rounded-lg text-sm font-medium transition-all duration-300 relative ${billingPeriod === 'yearly'
                  ? 'bg-gradient-to-br from-accent-strong to-accent text-white shadow-[0_10px_18px_rgba(8,12,18,0.35)]'
                  : 'text-gray-400 hover:text-white'
                  }`}
              >
                {t('membership.yearly', 'Yearly')}
                <span className="absolute -top-2.5 -right-2.5 bg-accent-warm text-slate-900 text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-sm">
                  {t('membership.savePercent', { percent: 17 })}
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* 等级卡片 */}
        <div className={`px-6 pb-6 grid gap-4 items-stretch ${tiers.length === 1 ? 'max-w-md mx-auto' : 'md:grid-cols-2'}`}>
          {tiers.map((tier) => {
            const price = billingPeriod === 'yearly' ? tier.price_yearly : tier.price_monthly;
            const isCurrentTier = tier.name === currentTier;
            const isPro = tier.name === 'pro';
            
            // 获取当前用户等级的 priority
            const currentTierData = tiers.find(t => t.name === currentTier);
            const currentPriority = currentTierData?.priority ?? -1;
            // 如果目标等级 priority <= 当前等级，则不能升级（降级或同级）
            const isLowerOrSameTier = tier.priority <= currentPriority;

            return (
              <div
                key={tier.name}
                className={`group relative rounded-2xl p-5 transition-all duration-300 flex flex-col h-full ${isPro
                  ? 'bg-gradient-to-b from-surface-highlight/60 to-surface border-2 border-accent-warm/30 hover:border-accent-warm/50 hover:shadow-[0_16px_30px_rgba(8,12,18,0.35)]'
                  : 'bg-surface-highlight/50 border border-glass-border hover:border-slate-500/30 hover:bg-surface-highlight/70'
                  }`}
              >
                {isPro && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <div className="bg-gradient-to-r from-accent-warm to-accent-soft text-slate-900 text-xs font-bold px-3 py-1 rounded-full shadow-[0_8px_16px_rgba(8,12,18,0.35)] flex items-center gap-1">
                      <FaStar className="w-3 h-3" />
                      {t('membership.recommended', 'Recommended')}
                    </div>
                  </div>
                )}

                <div className="mb-4">
                  <h3 className={`text-base font-medium mb-1 ${isPro ? 'text-white' : 'text-gray-300'}`}>
                    {tier.display_name}
                  </h3>
                  <div className="flex items-baseline gap-1">
                    <span className="text-2xl font-bold text-white tracking-tight">
                      ${price}
                    </span>
                    <span className="text-gray-500 text-sm">
                      /{billingPeriod === 'yearly' ? t('membership.year', 'Year') : t('membership.month', 'Month')}
                    </span>
                  </div>
                </div>

                <div className={`w-full h-px ${isPro ? 'bg-gradient-to-r from-accent/40 to-transparent' : 'bg-white/5'} mb-4`} />

                <ul className="space-y-2.5 mb-5 flex-1">
                  {tier.features.map((feature, idx) => (
                    <li
                      key={idx}
                      className="flex items-start gap-2.5 text-sm text-gray-300"
                    >
                      <div className={`mt-0.5 w-4 h-4 rounded-full flex items-center justify-center shrink-0 ${isPro ? 'bg-accent/20 text-accent' : 'bg-surface-highlight/70 text-gray-400'
                        }`}>
                        <FaCheck size={8} />
                      </div>
                      <span className="leading-5">{getTranslatedFeature(feature)}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleUpgradeClick(tier.name)}
                  disabled={loadingTier !== null || isLowerOrSameTier}
                  className={`w-full py-3 rounded-xl font-bold text-sm transition-all duration-300 shadow-lg mt-auto ${isLowerOrSameTier
                    ? 'bg-white/5 text-gray-400 cursor-not-allowed border border-white/5'
                    : isPro
                      ? 'bg-gradient-to-r from-accent-strong to-accent text-white hover:shadow-[0_12px_24px_rgba(8,12,18,0.35)] hover:scale-[1.02]'
                      : 'bg-surface-highlight/80 text-white hover:bg-surface-highlight hover:scale-[1.02]'
                    }`}
                >
                  {isCurrentTier
                    ? t('membership.currentPlan', 'Current Plan')
                    : isLowerOrSameTier
                      ? t('membership.alreadyHigher', 'Already Higher Tier')
                      : loadingTier === tier.name
                        ? t('common.processing', 'Processing...')
                        : t('membership.upgradeNow', 'Upgrade Now')}
                </button>
              </div>
            );
          })}
        </div>

        {/* 支付选项弹窗 */}
        {showPaymentOptions && nowpaymentsOptions.length > 0 && (
          <div className="fixed inset-0 z-60 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60" onClick={() => setShowPaymentOptions(false)} />
            <div className="relative bg-surface/95 backdrop-blur-xl rounded-2xl w-full max-w-md p-6 border border-glass-border shadow-2xl">
              <button
                onClick={() => setShowPaymentOptions(false)}
                className="absolute top-4 right-4 p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/5"
              >
                <FaTimes size={16} />
              </button>

              <h3 className="text-lg font-bold text-white mb-1">
                {t('payment.selectPaymentMethod')}
              </h3>
              <p className="text-gray-400 text-sm mb-5">
                {t('payment.selectNetworkAndToken')}
              </p>

              <div className="space-y-4">
                <div>
                  <label className="text-xs text-gray-400 mb-2 block">{t('payment.nowpayments.networkLabel')}</label>
                  <select
                    value={selectedChainId || ''}
                    onChange={(event) => {
                      const nextChainId = event.target.value;
                      setSelectedChainId(nextChainId);
                      const nextChain = nowpaymentsOptions.find((chain) => chain.id === nextChainId);
                      const nextTokenSymbol = nextChain?.tokens ? Object.keys(nextChain.tokens)[0] : null;
                      setSelectedTokenSymbol(nextTokenSymbol);
                    }}
                    className="w-full bg-surface-highlight/50 border border-glass-border rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-accent"
                  >
                    {nowpaymentsOptions.map((chain) => (
                      <option key={chain.id} value={chain.id} className="bg-slate-900">
                        {chain.label || chain.id}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-xs text-gray-400 mb-2 block">{t('payment.nowpayments.tokenLabel')}</label>
                  <select
                    value={selectedTokenSymbol || ''}
                    onChange={(event) => setSelectedTokenSymbol(event.target.value)}
                    className="w-full bg-surface-highlight/50 border border-glass-border rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-accent"
                  >
                    {selectedChain?.tokens &&
                      Object.keys(selectedChain.tokens).map((symbol) => (
                        <option key={symbol} value={symbol} className="bg-slate-900">
                          {symbol}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="pt-2 border-t border-glass-border">
                  <div className="flex justify-between text-sm mb-4">
                    <span className="text-gray-400">{t('payment.total')}</span>
                    <span className="text-white font-bold">${getPendingTierPrice()}</span>
                  </div>
                  <button
                    onClick={handleConfirmPayment}
                    disabled={loadingTier !== null}
                    className="w-full py-3 rounded-xl font-bold text-sm bg-gradient-to-r from-accent-strong to-accent text-white hover:shadow-[0_12px_24px_rgba(8,12,18,0.35)] transition-all"
                  >
                    {loadingTier ? t('common.processing') : t('payment.confirmPayment')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showCheckout && checkoutInfo && (
          <div className="fixed inset-0 z-70 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60" onClick={() => setShowCheckout(false)} />
            <div className="relative bg-surface/95 backdrop-blur-xl rounded-2xl w-full max-w-md p-6 border border-glass-border shadow-2xl">
              <button
                onClick={() => setShowCheckout(false)}
                className="absolute top-4 right-4 p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/5"
              >
                <FaTimes size={16} />
              </button>

              <h3 className="text-lg font-bold text-white mb-1">
                {t('payment.nowpayments.checkoutTitle')}
              </h3>
              <p className="text-gray-400 text-sm">
                {t('payment.nowpayments.checkoutSubtitle')}
              </p>

              <div className="mt-4 rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-slate-200">
                <div className="flex justify-between">
                  <span className="text-gray-400">{t('payment.nowpayments.amount')}</span>
                  <span className="text-white font-semibold">
                    {checkoutInfo.amount} {checkoutInfo.currency}
                  </span>
                </div>
                <div className="flex justify-between mt-2">
                  <span className="text-gray-400">{t('payment.nowpayments.payCurrency')}</span>
                  <span className="text-white font-semibold">{checkoutInfo.payLabel}</span>
                </div>
              </div>

              {checkoutInfo.popupBlocked && (
                <div className="mt-3 text-xs text-amber-300">
                  {t('payment.nowpayments.popupBlocked')}
                </div>
              )}

              <div className="mt-4 flex items-center gap-3">
                <button
                  onClick={handleOpenPaymentPage}
                  className="flex-1 rounded-xl bg-accent/80 hover:bg-accent text-white py-2.5 text-sm font-semibold transition-colors"
                  disabled={!checkoutInfo.paymentUrl}
                >
                  {t('payment.nowpayments.open')}
                </button>
                <button
                  onClick={() => handleCopy(checkoutInfo.paymentUrl, setCheckoutCopied)}
                  className="rounded-xl border border-white/10 px-4 py-2.5 text-sm text-slate-200 hover:text-white"
                  disabled={!checkoutInfo.paymentUrl}
                >
                  {checkoutCopied ? t('payment.nowpayments.copied') : t('payment.nowpayments.copy')}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
